/**
 * The six platform-console destinations, built once for both shells.
 *
 * ── Why this is shared rather than inlined twice ────────────────────────
 *
 * `App.tsx` (desktop deck) and `MobileShell.tsx` both mount `BudgetBillingView`
 * and previously each spelled out its page props. That duplication already
 * caused one real bug — the two shells disagreed about how to route, so every
 * mobile deep link opened the wrong page, which is why `parseBudgetBillingPath`
 * was centralised. Phase 1 grows the prop set from three pages to five AND adds
 * per-area capability plumbing to each, so a second copy would be the same
 * mistake with more surface.
 *
 * The capability props are the part most worth keeping single-sourced: which
 * area gates which control is a security statement, and two copies of it is how
 * a control ends up editable on one viewport and not the other.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import {
  CustomersList,
  CustomerDetail,
  PlatformOverview,
  PlatformSettings,
  PropagatePresetDialog,
  PlanLinkedTenants,
  LandingSetting,
  ExpensiveSessions,
  DiskPressurePanel,
  StorageTopTenants,
  AuditLog,
  ManualBillingQueue,
} from '@papan/orgs/ui'
import {
  CatalogAdmin, PromotionsPanel, StuckWebhooksPanel, DunningPanel,
} from '@papan/billing/ui'
import { LauncherAdmin } from '@papan/launcher/ui'
import { ReadOnlyNotice } from '@papan/orgs/ui'
import { canEditPlatformArea, type BudgetBillingAccess } from '@papan/core-ui/ui'

export interface PlatformPagesInput {
  access: BudgetBillingAccess
  isPlatformAdmin: boolean
  /** The workspace the admin is currently in — flagged "current" in the
   *  customers list, so an edit there is not mistaken for an org-level setting. */
  activeTenantId?: string
  /** Set when the URL names one customer (`.../customers/<id>`) — renders the
   *  detail page instead of the list. */
  customerId?: string
  /** Set when the URL names one plan (`.../pricebook/<planId>`) — opens that
   *  plan's editor instead of leaving the price book on the list. */
  planId?: string
  /** Navigate to one customer's detail page. */
  onOpenCustomer: (tenantId: string) => void
  /** Back to the list. */
  onCloseCustomer: () => void
  /** Open one plan's editor. */
  onOpenPlan: (planId: string) => void
  /** Back to the price book. */
  onClosePlan: () => void
  /** Jump to the Operations queue. Overview's alert tiles are links, not
   *  labels — a count is only useful beside the queue that resolves it. */
  onOpenOperations: () => void
  /** Jump to Platform settings. Same rule as the alert tiles: Overview's grace
   *  ceiling states a CONFIGURED number, and until it linked to the control it
   *  carried a tooltip naming an environment variable instead. */
  onOpenSettings: () => void
}

/** The six `BudgetBillingView` page props. Spread straight into the view. */
export function buildPlatformPages({
  access,
  isPlatformAdmin,
  activeTenantId,
  customerId,
  planId,
  onOpenCustomer,
  onCloseCustomer,
  onOpenPlan,
  onClosePlan,
  onOpenOperations,
  onOpenSettings,
}: PlatformPagesInput) {
  return {
    overviewPage: (
      <PlatformOverview
        onOpenCustomer={onOpenCustomer}
        onOpenOperations={onOpenOperations}
        onOpenSettings={onOpenSettings}
      />
    ),

    // List or detail, decided by the URL rather than by component state — see
    // `BudgetBillingRoute.tenantId`. That is what makes a customer linkable and
    // keeps the back button working.
    customersPage: customerId ? (
      <CustomerDetail
        tenantId={customerId}
        canEdit={canEditPlatformArea(access, 'customers')}
        // Re-pricing is a PRICE BOOK permission even though it renders here.
        canEditPricing={canEditPlatformArea(access, 'pricing')}
        // And chat-cost visibility is a SETTINGS permission even though it
        // renders here (0174). Same rule, third instance: gate by what the
        // action does. This one decides who may see the platform's own cost of
        // goods, which is the same subject as the deployment-wide setting rather
        // than a fact about this customer — so it is governed by the `settings`
        // grant. (Pre-2026-09-03 this also kept a `member` seat — then
        // `customers: 'write'`, `settings: 'read'` — from reaching it one tenant
        // at a time; member now holds `settings:write`, so the read-only
        // viewer/guest tier is the live boundary.)
        canEditSettings={canEditPlatformArea(access, 'settings')}
        // D6: the launcher, on the customer's own page and WITHOUT its picker.
        // Composed here because `@papan/orgs` may not depend on
        // `@papan/launcher` — the same slot shape as the metered-pricing grid.
        launcherSection={
          <LauncherAdmin isPlatformAdmin={isPlatformAdmin} canEdit={canEditPlatformArea(access, 'customers')} forTenant={{ id: customerId }} />
        }
        onBack={onCloseCustomer}
      />
    ) : (
      <CustomersList
        isPlatformAdmin={isPlatformAdmin}
        activeTenantId={activeTenantId}
        canCreate={canEditPlatformArea(access, 'customers')}
        onOpen={onOpenCustomer}
      />
    ),

    // Phase 1 unified the DESTINATION; phase 4 merges the CONTENT. Reading
    // order follows the resolution chain: metered defaults (what a rate falls
    // back to) → plans, with their per-plan overrides folded into the same grid
    // → credit packs → Publish. Deployment settings sit last, below Publish,
    // because they are platform configuration rather than prices — phase 5
    // moves them out entirely.
    //
    // The two components are composed rather than merged into one file:
    // `CatalogAdmin` is in `@papan/billing` and the pricing grid is in
    // `@papan/orgs`, which depends on billing, so importing across would close
    // a cycle. `CatalogAdmin` takes the grid as a slot instead.
    pricebookPage: (
      <PriceBook
        access={access}
        isPlatformAdmin={isPlatformAdmin}
        openPlanId={planId}
        onOpenPlan={onOpenPlan}
        onClosePlan={onClosePlan}
      />
    ),

    // The triage queues, in the order an operator works them: who is not
    // paying, what broke on the money path, what is burning budget right now,
    // and the record of every platform-level change.
    operationsPage: (
      <div>
        {/* Every other destination opens with its name and a sentence saying
            what it is for; this one opened straight into "Payment problems",
            so the only thing telling an operator where they were was the nav
            item they had already clicked. Four unrelated queues need the
            sentence more than a single-purpose screen does, not less. */}
        <OperationsHeader />
        <DunningPanel onOpenCustomer={onOpenCustomer} />
        {/* Manual billing's own unpaid queue, immediately beneath the provider's.
            They are separate because their rows mean different things — Stripe
            resolves one, a person resolves the other — and on any one deployment
            only one of them has rows, decided by `plan_authority`.

            Gated `customers` and NOT `operations`, deliberately: a `member` seat
            holds `operations:write` and so may work this queue, but re-planning a
            customer is a `customers:write` action. The button is the only thing
            that gate hides; the list is `operations:read` like the destination. */}
        <ManualBillingQueue
          onOpenCustomer={onOpenCustomer}
          canEdit={canEditPlatformArea(access, 'customers')}
        />
        <StuckWebhooksPanel canEdit={canEditPlatformArea(access, 'operations')} />
        {/* MOVED here from beneath the customers list (phase 6).
            ────────────────────────────────────────────────────────────────
            What is left in this panel after the phase-8 UI pass is redeemable
            promo CODES and campaign revocation — things that belong to NO
            particular customer. Sitting under "All customers" it read as part
            of that list, which is the filing mistake the redesign exists to
            remove: the per-tenant halves (granting a promo, adjusting credit)
            already moved onto the customer's own Plan & billing tab for exactly
            that reason.

            Operations is where cross-customer work lives, and revoking a
            campaign is cross-customer work. Still `customers`-gated, because
            gating follows what an action DOES — a promo grants spendable money
            to customers — and not where it renders. */}
        <PromotionsPanel canEdit={canEditPlatformArea(access, 'customers')} />
        <ExpensiveSessions onOpenCustomer={onOpenCustomer} />
        {/* Host disk pressure (#450). Operations, NOT the tenant: a host running
            out of disk is not something a tenant can act on — #450's Routing
            section says so explicitly, and the existing `budgetScope: 'storage'`
            notification already answers the tenant's different question ("your
            org hit its quota").

            Gated `operations:read` server-side, the same area that gates this
            destination's nav item — so the page's own gate implies its content's.
            No `canEdit` prop because there is nothing to edit: this panel is a
            READING of the alarm, and every lever it might offer (run the GC,
            reclaim markers) belongs to the runbook and the sweeps that own it. */}
        <DiskPressurePanel />
        {/* "Which tenant is filling the disk" — the screen #64 shipped an API
            and CLI for but never a panel. Same `operations:read` gate as the
            rest of this destination. */}
        <StorageTopTenants onOpenCustomer={onOpenCustomer} />
        {/* Platform-scope rows only (`tenant_id IS NULL`): pricing, catalog,
            billing settings and `platform_admin_grant`. A tenant's own history
            is on that customer's Activity tab, not here. Gated `operations:read`
            server-side — the same area that gates this destination's nav item,
            which is what makes the page's own gate imply its content's. */}
        <div className="mt-8">
          <PlatformActivity />
        </div>
      </div>
    ),

    // Deployment-wide configuration, and the numbers that decide the margin.
    // Each editable number sits beside the outcome it produces: the credit rate
    // against realised margin, the grace ceiling against what it has already
    // given away this period.
    settingsPage: (
      <PlatformSettings canEdit={canEditPlatformArea(access, 'settings')} />
    ),

    // Launcher curation, plus the deployment's landing surface above it.
    //
    // Folded in here rather than supplied by each shell — which is what it used
    // to be, on the grounds that it is not part of this set's "audience-shaped"
    // pages. That stopped being the right call the moment it needed a
    // capability prop: this file exists because "which area gates which
    // control is a security statement, and two copies of it is how a control
    // ends up editable on one viewport and not the other". The landing is
    // exactly such a control, so its gate is single-sourced here.
    //
    // ⚠ `settings`, NOT `customers`. The page around it (the tiles) is
    // `customers` — `canEdit` below, matching the routes (#337); the landing
    // switch is not. A `member` seat holds `customers: 'write'` and only
    // `settings: 'read'` — it may curate a customer's tiles and may not
    // retarget every user's home screen. Same split as `canEditPricing` on
    // CustomerDetail above.
    launcherPage: (
      <LauncherAdmin
        isPlatformAdmin={isPlatformAdmin}
        canEdit={canEditPlatformArea(access, 'customers')}
        landingSection={<LandingSetting canEdit={canEditPlatformArea(access, 'settings')} />}
      />
    ),
  }
}

/**
 * The merged price book's single heading.
 *
 * Its own component because `buildPlatformPages` is a plain function and the
 * copy needs `useTranslation`. The read-only notice lives here too rather than
 * inside either half: a read-only seat (viewer/guest — `pricing:read` and
 * `settings:read`) has every control below locked, so saying it once at the top
 * beats two notices mid-page. (The write tier — owner/admin/member — edits the
 * whole screen; member joined it 2026-09-03.)
 */
/**
 * The price book, plus the one value on it that is NOT a price.
 *
 * ── Why this wrapper exists ──────────────────────────────────────────────
 *
 * `buildPlatformPages` is a plain function, so it cannot hold the fetch for
 * `platform_settings.default_plan_id` — the pointer that decides which preset a
 * console-created customer is seeded from. And `CatalogAdmin` cannot fetch it
 * itself: the value's client lives in `@papan/orgs`, which depends on
 * `@papan/billing`, so reaching for it there would either close that cycle or
 * mean a second client for a route another package already owns. This file is
 * the composition root that can see both, which is exactly where the wiring
 * belongs — the same reasoning as the metered-pricing slot below.
 *
 * ── The two capabilities on one screen ───────────────────────────────────
 *
 * Everything else here is `pricing`. "Set as default" writes `platform_settings`
 * and is therefore `settings:write`, so it is passed as its own prop rather than
 * folded into `canEditPricing`: a `member` seat holds pricing WRITE and settings
 * READ, and must be able to re-price a preset without being able to point
 * provisioning at it. Gate by what the action does, not by where it renders.
 */
function PriceBook({
  access,
  isPlatformAdmin,
  openPlanId,
  onOpenPlan,
  onClosePlan,
}: {
  access: BudgetBillingAccess
  isPlatformAdmin: boolean
  /** From the URL, not from state — see `BudgetBillingRoute.planId`. */
  openPlanId?: string
  onOpenPlan: (planId: string) => void
  onClosePlan: () => void
}) {
  const canEditPricing = canEditPlatformArea(access, 'pricing')
  const canEditSettings = canEditPlatformArea(access, 'settings')
  // `undefined` while unknown, so the column stays absent rather than briefly
  // claiming "no default configured" — which is a real, different state.
  const [defaultPresetId, setDefaultPresetId] = useState<string | null | undefined>(undefined)
  /** The preset whose propagation prompt is open, or null. `mode` picks the
   *  dialog behaviour: 'reset' (manual "Apply to tenants") vs 'propagate'
   *  (auto-apply after a grant-affecting plan save — F3). */
  const [propagating, setPropagating] = useState<{ id: string; name: string; mode: 'reset' | 'propagate' } | null>(null)

  const load = useCallback(async () => {
    try {
      const { budgetApi } = await import('@papan/orgs/client')
      const res = await budgetApi.getPlatformSettings()
      setDefaultPresetId(res.settings.defaultPlanId ?? null)
    } catch {
      // Advisory: the price book must still render every price if this one read
      // fails. Left `undefined`, so the column is hidden rather than wrong.
      setDefaultPresetId(undefined)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  return (
    <>
      {propagating && (
        <PropagatePresetDialog
          planId={propagating.id}
          planName={propagating.name}
          mode={propagating.mode}
          onClose={() => setPropagating(null)}
        />
      )}
    <CatalogAdmin
      isPlatformAdmin={isPlatformAdmin}
      canEditPricing={canEditPricing}
      header={<PriceBookHeader canEdit={canEditPricing} />}
      defaultPresetId={defaultPresetId}
      onSetDefaultPreset={canEditSettings
        ? async (planId) => {
            const { budgetApi } = await import('@papan/orgs/client')
            await budgetApi.setPlatformSettings({ defaultPlanId: planId })
            await load()
          }
        : undefined}
      // Propagation writes CUSTOMERS' ceilings, so it is gated `customers:write`
      // — not the page's `pricing` lever. Gate by what the action does.
      onPropagate={canEditPlatformArea(access, 'customers')
        ? (p) => setPropagating({ ...p, mode: 'reset' })
        : undefined}
      // Auto-apply (F3): after a grant-affecting plan save, offer to push the new
      // values to the tenants on the plan — preserving overwritten axes. Same
      // customers:write gate as manual propagation.
      onGrantsChanged={canEditPlatformArea(access, 'customers')
        ? (p) => setPropagating({ ...p, mode: 'propagate' })
        : undefined}
      // The linked-tenant list + "Link tenants" control inside the plan modal's
      // Tenant-specific controls. A slot because it reads `budgetApi` in
      // `@papan/orgs` (billing cannot import orgs). Linking writes a plan
      // override, so it is gated `customers:write`.
      renderPlanTenants={(p) => (
        <PlanLinkedTenants plan={p} canEdit={canEditPlatformArea(access, 'customers')} />
      )}
      openPlanId={openPlanId}
      onOpenPlan={onOpenPlan}
      onClosePlan={onClosePlan}
    />
    </>
  )
}

function PriceBookHeader({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation('core-ui')
  return (
    <>
      <h2 className="themed-text text-lg font-medium mb-1">
        {t('settings.platform.nav.pricebook', { defaultValue: 'Price book' })}
      </h2>
      <p className="themed-text-muted text-sm mb-4">
        {t('settings.platform.pricebook.desc', {
          defaultValue:
            'What everything costs, in the order it resolves: the metered defaults first, then the plans that override them, then the credit packs — and finally Publish, which makes it all purchasable.',
        })}
      </p>
      {!canEdit && <ReadOnlyNotice />}
    </>
  )
}

/**
 * Operations' own heading. Its own component for the same reason
 * `PriceBookHeader` is — `buildPlatformPages` is a plain function and this copy
 * needs `useTranslation`.
 */
function OperationsHeader() {
  const { t } = useTranslation('core-ui')
  return (
    <>
      <h2 className="themed-text text-lg font-medium mb-1">
        {t('settings.platform.nav.operations', { defaultValue: 'Operations' })}
      </h2>
      <p className="themed-text-muted text-sm mb-4">
        {t('settings.platform.operations.desc', {
          defaultValue:
            'The queues to work when something is wrong, in the order to work them: who is not paying, what broke on the money path, what is burning budget right now — and the record of every platform-level change.',
        })}
      </p>
    </>
  )
}

/** The platform-scope audit log, with copy that needs `useTranslation`. */
function PlatformActivity() {
  const { t } = useTranslation('core-ui')
  return (
    <AuditLog
      title={t('settings.platform.activity.title', { defaultValue: 'Platform activity' })}
      desc={t('settings.platform.activity.desc', {
        defaultValue:
          'Every pricing, catalog and platform-settings change — including who was granted platform administration. A tenant\'s own history is on their Activity tab.',
      })}
    />
  )
}
